import React from 'react';
import styled from 'styled-components';
import { Button, IconButton } from '../../../Button';
import VipIcon from '../../../../Icons/VipIcons';
import { CARDS } from '../../../../../constants/staticTexts';

// Base card container
const ListCardContainer = styled.div`
  background: ${({theme, $disabled}) => $disabled ? theme.colors.bg3 : theme.colors.bg2};
  border: 1px solid ${({theme}) => theme.colors.divider};
  border-radius: ${({theme}) => theme.borderRadius.md};
  padding: ${({$size}) => {
    switch ($size) {
      case 'large': return '20px';
      case 'medium': return '16px';
      case 'compact': return '12px';
      default: return '16px';
    }
  }};
  transition: all 0.2s ease;
  cursor: ${({$interactive, $disabled}) => $disabled ? 'not-allowed' : ($interactive ? 'pointer' : 'default')};
  position: relative;
  opacity: ${({$disabled}) => $disabled ? 0.6 : 1};
  
  &:hover {
    ${({$interactive, $disabled, theme}) => $interactive && !$disabled && `
      background: ${theme.colors.primaryGradientLight};
      border-color: ${theme.colors.primaryPink}40;
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    `}
  }
  
  ${({$size}) => {
    switch ($size) {
      case 'large':
        return `
          min-height: 160px;
          @media (max-width: 768px) {
            min-height: 140px;
            padding: 16px;
          }
        `;
      case 'medium':
        return `
          min-height: 120px;
          @media (max-width: 768px) {
            min-height: 100px;
            padding: 14px;
          }
        `;
      case 'compact':
        return `
          min-height: 40px;
          padding: 8px 12px;
          flex: 1 0 auto;
          @media (max-width: 768px) {
            min-height: 36px;
            padding: 6px 10px;
          }
        `;
      default:
        return '';
    }
  }}
`;

// Header section with icon and title
const ListHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({$size}) => {
    switch ($size) {
      case 'large': return '16px';
      case 'medium': return '12px';
      case 'compact': return '8px';
      default: return '12px';
    }
  }};
  margin-bottom: ${({$size}) => {
    switch ($size) {
      case 'large': return '16px';
      case 'medium': return '12px';
      case 'compact': return '8px';
      default: return '12px';
    }
  }};
`;

// List icon container
const ListIcon = styled.div`
  width: ${({$size}) => {
    switch ($size) {
      case 'large': return '48px';
      case 'medium': return '40px';
      case 'compact': return '32px';
      default: return '40px';
    }
  }};
  height: ${({$size}) => {
    switch ($size) {
      case 'large': return '48px';
      case 'medium': return '40px';
      case 'compact': return '32px';
      default: return '40px';
    }
  }};
  background: ${({theme}) => theme.colors.primaryGradient};
  border-radius: ${({theme}) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

// List info container
const ListInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

// List title
const ListTitle = styled.h3`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.lg;
      case 'medium': return theme.fontSizes.md;
      case 'compact': return theme.fontSizes.sm;
      default: return theme.fontSizes.md;
    }
  }};
  font-weight: 600;
  color: ${({theme}) => theme.colors.text};
  margin: 0 0 4px 0;
  line-height: 1.3;
  ${({$size}) => $size !== 'compact' ? `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  ` : `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

// List description
const ListDescription = styled.p`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.sm;
      case 'medium': return theme.fontSizes.xs;
      case 'compact': return theme.fontSizes.xs;
      default: return theme.fontSizes.sm;
    }
  }};
  color: ${({theme}) => theme.colors.textSecondary};
  margin: 0 0 8px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({$size}) => {
    switch ($size) {
      case 'large': return 3;
      case 'medium': return 2;
      case 'compact': return 1;
      default: return 2;
    }
  }};
  -webkit-box-orient: vertical;
  
  ${({$size}) => $size === 'compact' && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

// Badges container
const BadgesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

// Badge component
const Badge = styled.span`
  background: ${({$variant, theme}) => {
    switch ($variant) {
      case 'system': return theme.colors.info;
      case 'private': return theme.colors.warning;
      case 'public': return theme.colors.success;
      default: return theme.colors.primaryPink;
    }
  }};
  color: white;
  font-size: ${({$size}) => {
    switch ($size) {
      case 'large': return '11px';
      case 'medium': return '10px';
      case 'compact': return '9px';
      default: return '10px';
    }
  }};
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Stats container
const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({$size}) => {
    switch ($size) {
      case 'large': return '16px';
      case 'medium': return '12px';
      case 'compact': return '8px';
      default: return '12px';
    }
  }};
  margin-bottom: ${({$size}) => $size === 'large' ? '12px' : '8px'};
  flex-wrap: wrap;
`;

// Individual stat
const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.sm;
      case 'medium': return theme.fontSizes.xs;
      case 'compact': return theme.fontSizes.xs;
      default: return theme.fontSizes.sm;
    }
  }};
  color: ${({theme}) => theme.colors.textSecondary};
  
  svg {
    color: ${({theme}) => theme.colors.primaryPink};
  }
`;

// Actions container
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  flex-wrap: wrap;
  
  ${({$size}) => $size === 'compact' && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

// Action button
const ACTION_SIZES = { compact: 'small', medium: undefined, large: 'large' };

const ActionButton = styled(Button).attrs(({ $size }) => ({
  $size: ACTION_SIZES[$size],
}))``;

// Checkbox container (for compact cards)
const CheckboxContainer = styled.div`
  cursor: pointer;
  padding: 2px;
  border-radius: 50px;
  transition: all 0.2s ease;
  background: ${({$selected, theme}) => $selected ? theme.colors.primary : 'white'};
  color: ${({$selected, theme}) => $selected ? 'white' : theme.colors.textSecondary};
  border: 1px solid ${({$selected, theme}) => $selected ? theme.colors.primary : theme.colors.divider};
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: ${({theme}) => theme.colors.primary};
    background: ${({$selected, theme}) => $selected ? theme.colors.primaryDark : theme.colors.primaryGradientLight};
  }
`;

// Context menu button (for compact cards)
const ContextMenuButton = styled(IconButton).attrs({ $size: 'small' })`
  position: absolute;
  top: 4px;
  right: 4px;
  color: ${({theme}) => theme.colors.textSecondary};

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.colors.text};
  }
`;

const ListCard = ({
  list,
  size = 'medium',
  interactive = true,
  showActions = true,
  actions = [],
  selected = false,
  disabled = false,
  onSelect,
  onClick,
  onActionClick,
  className,
  ...props
}) => {
  const getIconName = (listType) => {
    switch (listType?.toLowerCase()) {
      case 'system':
        return 'Settings';
      case 'favorites':
        return 'Heart';
      case 'blocked':
        return 'UserX';
      case 'subscribers':
        return 'Users';
      default:
        return 'List';
    }
  };

  const getBadgeVariant = (listType, isPrivate) => {
    if (listType?.toLowerCase() === 'system') return 'system';
    if (isPrivate) return 'private';
    return 'public';
  };

  const getBadgeText = (listType, isPrivate) => {
    if (listType?.toLowerCase() === 'system') return CARDS.LIST_BADGE_SYSTEM;
    if (isPrivate) return CARDS.LIST_BADGE_PRIVATE;
    return CARDS.LIST_BADGE_PUBLIC;
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect(list);
    }
  };

  const handleClick = () => {
    if (disabled) return; // Don't handle clicks when disabled
    
    // For compact cards, clicking anywhere should trigger selection
    if (size === 'compact' && onSelect) {
      onSelect(list);
    } else if (interactive && onClick) {
      onClick(list);
    }
  };

  const handleActionClick = (action, event) => {
    event.stopPropagation();
    if (onActionClick) {
      onActionClick(action, list);
    }
  };

  return (
    <ListCardContainer
      $size={size}
      $interactive={interactive}
      $disabled={disabled}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {/* Compact card layout */}
      {size === 'compact' ? (
        <ListHeader $size={size} style={{ marginBottom: 0, alignItems: 'center' }}>
          <ListInfo style={{ marginRight: '12px' }}>
            <ListTitle $size={size} style={{ margin: 0, lineHeight: 1.2 }}>
              {list.name}
            </ListTitle>
          </ListInfo>
          
          <CheckboxContainer
            $selected={selected}
            onClick={handleCheckboxClick}
          >
            {selected && (
              <VipIcon 
                name="Checkmark" 
                size={12} 
              />
            )}
          </CheckboxContainer>
        </ListHeader>
      ) : (
        <>
          {/* Full card layout for medium and large sizes */}
          {actions.length > 0 && (
            <ContextMenuButton aria-label="More actions" onClick={(e) => handleActionClick('menu', e)}>
              <VipIcon name="MoreHorizontal" size={16} />
            </ContextMenuButton>
          )}
          
          <ListHeader $size={size}>
            <ListIcon $size={size}>
              <VipIcon 
                name={getIconName(list.type)} 
                size={size === 'large' ? 24 : 20} 
              />
            </ListIcon>
            
            <ListInfo>
              <ListTitle $size={size}>{list.name}</ListTitle>
              
              {list.description && (
                <ListDescription $size={size}>
                  {list.description}
                </ListDescription>
              )}
              
              <BadgesContainer>
                <Badge 
                  $variant={getBadgeVariant(list.type, list.isPrivate)}
                  $size={size}
                >
                  {getBadgeText(list.type, list.isPrivate)}
                </Badge>
                {list.isOwner && (
                  <Badge $variant="default" $size={size}>{CARDS.LIST_BADGE_OWNER}</Badge>
                )}
              </BadgesContainer>
            </ListInfo>
          </ListHeader>
          
          <StatsContainer $size={size}>
            <Stat $size={size}>
              <VipIcon name="Users" size={14} />
              {list.memberCount || 0} {(list.memberCount || 0) !== 1 ? CARDS.LIST_MEMBERS : CARDS.LIST_MEMBER}
            </Stat>
            
            {size === 'large' && list.createdAt && (
              <Stat $size={size}>
                <VipIcon name="Calendar" size={14} />
                {CARDS.LIST_CREATED} {new Date(list.createdAt).toLocaleDateString()}
              </Stat>
            )}
            
            {size === 'large' && list.lastUpdated && (
              <Stat $size={size}>
                <VipIcon name="Clock" size={14} />
                {CARDS.LIST_UPDATED} {new Date(list.lastUpdated).toLocaleDateString()}
              </Stat>
            )}
          </StatsContainer>
          
          {showActions && actions.length > 0 && (
            <ActionsContainer $size={size}>
              {actions.map((action, index) => (
                <ActionButton
                  key={index}
                  $variant={action.variant || 'secondary'}
                  $size={size}
                  onClick={(e) => handleActionClick(action, e)}
                  disabled={action.disabled}
                >
                  {action.icon && (
                    <VipIcon 
                      name={action.icon} 
                      size={size === 'large' ? 16 : 14} 
                    />
                  )}
                  {action.label}
                </ActionButton>
              ))}
            </ActionsContainer>
          )}
        </>
      )}
    </ListCardContainer>
  );
};

export default ListCard;
